<template>
  <div>
    <div class="navigation">
      <div class="navigation_bottom">
        <img class="ai_assistant" ref="ai_assistant"
          src="https://mj-uploads.oss-cn-shenzhen.aliyuncs.com/public/aiDesign/web/AI_Ass/AI_Ass.png" alt="">
        <span ref="scroll_bottom" class="scroll_bottom iconfont" @click="scrollToBottom">&#xe685; 回到底部</span>
      </div>
      <div class="navigation_items" ref="navigation_items">
        <div class="main_input" ref="main_input">
          <!-- 图片上传区域 -->
          <div class="upload-section" @click="handleImageUpload">
            <div v-if="!uploadImages.length" class="upload-placeholder">
              <span class="upload-plus">+</span>
              <span class="upload-label">上传图片</span>
            </div>
            <div v-else class="uploaded-preview">
              <img :src="uploadImages[0]" alt="图片预览" class="thumbnail-img">
              <div class="image-count" v-if="uploadImages.length > 1">+{{ uploadImages.length - 1 }}</div>
              <div class="remove-btn" @click.stop="removeImages">×</div>
            </div>
          </div>

          <!-- 画面角度选择按钮组（5个下拉按钮） -->
          <div class="angle-buttons-container">
            <!-- 第一行：3个按钮 -->
            <div class="angle-row">
              <div
                v-for="index in 3"
                :key="'angle-' + (index - 1)"
                class="angle-dropdown"
                @click.stop="toggleAngleMenu(index - 1)"
              >
                <span class="option-label">画面角度{{ index }}</span>
                <span class="option-value">{{ selectedAngles[index - 1] }}</span>
                <span class="iconfont arrow">&#xe685;</span>
                <div v-show="showAngleMenus[index - 1]" class="dropdown-menu" @click.stop>
                  <div
                    v-for="option in angleOptions"
                    :key="option"
                    class="menu-item"
                    :class="{ active: selectedAngles[index - 1] === option }"
                    @click="selectAngle(index - 1, option)"
                  >
                    {{ option }}
                  </div>
                </div>
              </div>
            </div>
            <!-- 第二行：3个按钮（2个角度按钮 + 1个高级选项按钮） -->
            <div class="angle-row">
              <div
                v-for="index in 2"
                :key="'angle-' + (index + 2)"
                class="angle-dropdown"
                @click.stop="toggleAngleMenu(index + 2)"
              >
                <span class="option-label">画面角度{{ index + 3 }}</span>
                <span class="option-value">{{ selectedAngles[index + 2] }}</span>
                <span class="iconfont arrow">&#xe685;</span>
                <div v-show="showAngleMenus[index + 2]" class="dropdown-menu" @click.stop>
                  <div
                    v-for="option in angleOptions"
                    :key="option"
                    class="menu-item"
                    :class="{ active: selectedAngles[index + 2] === option }"
                    @click="selectAngle(index + 2, option)"
                  >
                    {{ option }}
                  </div>
                </div>
              </div>
              <!-- 高级选项按钮 -->
              <div class="advanced-options-btn" @click.stop="toggleAdvancedPanel">
                <span class="center-text">高级选项</span>
              </div>
            </div>
          </div>

          <button ref="up" class="sent iconfont" :class="{ 'has_input': canSend }" @click.stop="startTask" v-show="!scrollStatus" :disabled="!canSend || processing">
            <span v-if="!processing">&#xe688;</span>
            <span v-else class="rotating">&#xe6a4;</span>
          </button>
        </div>
        <!-- 放大后的发送按钮（绝对定位） -->
        <button ref="down" class="sent_expanded iconfont" :class="{ 'has_input': canSend }" @click.stop="startTask" v-show="scrollStatus" :disabled="!canSend || processing">
          <span v-if="!processing">&#xe688;</span>
          <span v-else class="rotating">&#xe6a4;</span>
        </button>
      </div>
    </div>

    <!-- 高级选项弹窗（全屏遮罩 + 毛玻璃） -->
    <transition name="panel-fade">
      <div v-if="showAdvancedPanel" class="advanced-panel-overlay" @click="showAdvancedPanel = false">
        <div class="advanced-panel-content" @click.stop>
          <div class="advanced-panel-header">
            <span class="panel-title">高级选项 · 多角度相机设置</span>
            <span class="panel-close-btn" @click="showAdvancedPanel = false">&times;</span>
          </div>
          <div class="advanced-panel-body">
            <div class="camera-item">
              <div class="camera-label">画面角度 1</div>
              <div class="camera-widget-wrapper">
                <CameraWidget3D
                  ref="cameraWidget1"
                  :imageUrl="uploadImages[0] || null"
                  :initialParams="angleParamsMap[selectedAngles[0]] || cameraParams[0]"
                  @camera-change="handleCameraChange(1, $event)"
                />
              </div>
              <div class="camera-sliders">
                <div class="slider-row">
                  <span class="slider-label">水平角度</span>
                  <input type="range" class="camera-slider" min="0" max="360" step="1"
                         :value="Math.round(cameraParams[0].horizontal_angle)"
                         @input="handleSliderChange(1, 'horizontal_angle', $event.target.value)" />
                  <span class="slider-value">{{ Math.round(cameraParams[0].horizontal_angle) }}°</span>
                </div>
                <div class="slider-row">
                  <span class="slider-label">垂直角度</span>
                  <input type="range" class="camera-slider" min="-30" max="60" step="1"
                         :value="Math.round(cameraParams[0].vertical_angle)"
                         @input="handleSliderChange(1, 'vertical_angle', $event.target.value)" />
                  <span class="slider-value">{{ Math.round(cameraParams[0].vertical_angle) }}°</span>
                </div>
                <div class="slider-row">
                  <span class="slider-label">距离</span>
                  <input type="range" class="camera-slider" min="0" max="10" step="0.1"
                         :value="cameraParams[0].zoom"
                         @input="handleSliderChange(1, 'zoom', $event.target.value)" />
                  <span class="slider-value">{{ Number(cameraParams[0].zoom).toFixed(1) }}</span>
                </div>
              </div>
            </div>
            <div class="camera-item">
              <div class="camera-label">画面角度 2</div>
              <div class="camera-widget-wrapper">
                <CameraWidget3D
                  ref="cameraWidget2"
                  :imageUrl="uploadImages[0] || null"
                  :initialParams="angleParamsMap[selectedAngles[1]] || cameraParams[1]"
                  @camera-change="handleCameraChange(2, $event)"
                />
              </div>
              <div class="camera-sliders">
                <div class="slider-row">
                  <span class="slider-label">水平角度</span>
                  <input type="range" class="camera-slider" min="0" max="360" step="1"
                         :value="Math.round(cameraParams[1].horizontal_angle)"
                         @input="handleSliderChange(2, 'horizontal_angle', $event.target.value)" />
                  <span class="slider-value">{{ Math.round(cameraParams[1].horizontal_angle) }}°</span>
                </div>
                <div class="slider-row">
                  <span class="slider-label">垂直角度</span>
                  <input type="range" class="camera-slider" min="-30" max="60" step="1"
                         :value="Math.round(cameraParams[1].vertical_angle)"
                         @input="handleSliderChange(2, 'vertical_angle', $event.target.value)" />
                  <span class="slider-value">{{ Math.round(cameraParams[1].vertical_angle) }}°</span>
                </div>
                <div class="slider-row">
                  <span class="slider-label">距离</span>
                  <input type="range" class="camera-slider" min="0" max="10" step="0.1"
                         :value="cameraParams[1].zoom"
                         @input="handleSliderChange(2, 'zoom', $event.target.value)" />
                  <span class="slider-value">{{ Number(cameraParams[1].zoom).toFixed(1) }}</span>
                </div>
              </div>
            </div>
            <div class="camera-item">
              <div class="camera-label">画面角度 3</div>
              <div class="camera-widget-wrapper">
                <CameraWidget3D
                  ref="cameraWidget3"
                  :imageUrl="uploadImages[0] || null"
                  :initialParams="angleParamsMap[selectedAngles[2]] || cameraParams[2]"
                  @camera-change="handleCameraChange(3, $event)"
                />
              </div>
              <div class="camera-sliders">
                <div class="slider-row">
                  <span class="slider-label">水平角度</span>
                  <input type="range" class="camera-slider" min="0" max="360" step="1"
                         :value="Math.round(cameraParams[2].horizontal_angle)"
                         @input="handleSliderChange(3, 'horizontal_angle', $event.target.value)" />
                  <span class="slider-value">{{ Math.round(cameraParams[2].horizontal_angle) }}°</span>
                </div>
                <div class="slider-row">
                  <span class="slider-label">垂直角度</span>
                  <input type="range" class="camera-slider" min="-30" max="60" step="1"
                         :value="Math.round(cameraParams[2].vertical_angle)"
                         @input="handleSliderChange(3, 'vertical_angle', $event.target.value)" />
                  <span class="slider-value">{{ Math.round(cameraParams[2].vertical_angle) }}°</span>
                </div>
                <div class="slider-row">
                  <span class="slider-label">距离</span>
                  <input type="range" class="camera-slider" min="0" max="10" step="0.1"
                         :value="cameraParams[2].zoom"
                         @input="handleSliderChange(3, 'zoom', $event.target.value)" />
                  <span class="slider-value">{{ Number(cameraParams[2].zoom).toFixed(1) }}</span>
                </div>
              </div>
            </div>
            <div class="camera-item">
              <div class="camera-label">画面角度 4</div>
              <div class="camera-widget-wrapper">
                <CameraWidget3D
                  ref="cameraWidget4"
                  :imageUrl="uploadImages[0] || null"
                  :initialParams="angleParamsMap[selectedAngles[3]] || cameraParams[3]"
                  @camera-change="handleCameraChange(4, $event)"
                />
              </div>
              <div class="camera-sliders">
                <div class="slider-row">
                  <span class="slider-label">水平角度</span>
                  <input type="range" class="camera-slider" min="0" max="360" step="1"
                         :value="Math.round(cameraParams[3].horizontal_angle)"
                         @input="handleSliderChange(4, 'horizontal_angle', $event.target.value)" />
                  <span class="slider-value">{{ Math.round(cameraParams[3].horizontal_angle) }}°</span>
                </div>
                <div class="slider-row">
                  <span class="slider-label">垂直角度</span>
                  <input type="range" class="camera-slider" min="-30" max="60" step="1"
                         :value="Math.round(cameraParams[3].vertical_angle)"
                         @input="handleSliderChange(4, 'vertical_angle', $event.target.value)" />
                  <span class="slider-value">{{ Math.round(cameraParams[3].vertical_angle) }}°</span>
                </div>
                <div class="slider-row">
                  <span class="slider-label">距离</span>
                  <input type="range" class="camera-slider" min="0" max="10" step="0.1"
                         :value="cameraParams[3].zoom"
                         @input="handleSliderChange(4, 'zoom', $event.target.value)" />
                  <span class="slider-value">{{ Number(cameraParams[3].zoom).toFixed(1) }}</span>
                </div>
              </div>
            </div>
            <div class="camera-item">
              <div class="camera-label">画面角度 5</div>
              <div class="camera-widget-wrapper">
                <CameraWidget3D
                  ref="cameraWidget5"
                  :imageUrl="uploadImages[0] || null"
                  :initialParams="angleParamsMap[selectedAngles[4]] || cameraParams[4]"
                  @camera-change="handleCameraChange(5, $event)"
                />
              </div>
              <div class="camera-sliders">
                <div class="slider-row">
                  <span class="slider-label">水平角度</span>
                  <input type="range" class="camera-slider" min="0" max="360" step="1"
                         :value="Math.round(cameraParams[4].horizontal_angle)"
                         @input="handleSliderChange(5, 'horizontal_angle', $event.target.value)" />
                  <span class="slider-value">{{ Math.round(cameraParams[4].horizontal_angle) }}°</span>
                </div>
                <div class="slider-row">
                  <span class="slider-label">垂直角度</span>
                  <input type="range" class="camera-slider" min="-30" max="60" step="1"
                         :value="Math.round(cameraParams[4].vertical_angle)"
                         @input="handleSliderChange(5, 'vertical_angle', $event.target.value)" />
                  <span class="slider-value">{{ Math.round(cameraParams[4].vertical_angle) }}°</span>
                </div>
                <div class="slider-row">
                  <span class="slider-label">距离</span>
                  <input type="range" class="camera-slider" min="0" max="10" step="0.1"
                         :value="cameraParams[4].zoom"
                         @input="handleSliderChange(5, 'zoom', $event.target.value)" />
                  <span class="slider-value">{{ Number(cameraParams[4].zoom).toFixed(1) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { Message } from 'element-ui'
import { getOssClient } from '@/utils/oss'
import { v4 as uuidv4 } from 'uuid'
import { genPrompt } from '@/api/prompt'
import { getLastUsedBaseUrl, cudaIndexNum } from '@/utils/comfyUiRequest'
import axios from 'axios'
import cuda from '@/utils/checkCuda'
import multiAngleWorkflow from '@/api/json_api/Qwen_multiangle.json'
import CameraWidget3D from './CameraWidget3D.vue'
import { getTaskEnd, getTaskStart } from '@/api/user'
import { MULTI_ANGLE } from '@/enums/common/modules'
import { getImageSizeByLoading } from '@/utils/helper/function'

import { OSS_ACCESS_KEY_ID, OSS_ACCESS_KEY_SECRET } from '@/utils/secretCrypto'
export default {
  name: 'NaviGation_MultiAngle',
  components: {
    CameraWidget3D
  },
  props: {
    initialChatHistory: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      textarea: '',
      uploadImages: [], // 上传的图片URL数组
      processing: false,
      multiAngleChatHistory: [],
      scrollStatus: false,
      // WebSocket
      ws: null,
      clientId: '',
      // 防止重复获取结果标志
      resultFetched: false,
      // 任务日志ID
      taskLogId: null,
      // 5个3D相机参数（初始值与selectedAngles预设角度对应）
      cameraParams: [
        { horizontal_angle: 335, vertical_angle: 20, zoom: 5 }, // 左侧俯视
        { horizontal_angle: 30, vertical_angle: 20, zoom: 5 }, // 右侧俯视
        { horizontal_angle: 335, vertical_angle: 0, zoom: 5 }, // 左侧平视
        { horizontal_angle: 30, vertical_angle: 0, zoom: 5 }, // 右侧平视
        { horizontal_angle: 335, vertical_angle: -15, zoom: 5 } // 左侧仰视
      ],
      // 节点名称映射（根据实际工作流节点）
      nodeNameMap: {
        79: 'ImageScale',
        95: 'CameraAngle1',
        100: 'CameraAngle2',
        101: 'CameraAngle3',
        102: 'CameraAngle4',
        146: 'CameraAngle5',
        126: 'LoadCLIP',
        130: 'LoadUNet',
        132: 'LoadVAE',
        134: 'TextEncoding',
        139: 'KSampler',
        140: 'VAEDecode',
        195: 'ImageBatch',
        191: 'UploadResult1',
        192: 'UploadResult2',
        193: 'UploadResult3',
        194: 'UploadResult4',
        196: 'UploadResult5'
      },
      currentExecutingNode: '', // 当前执行的节点名称
      showAdvancedPanel: false, // 高级选项悬浮窗显示状态
      // 画面角度选项（5个角度的选择）
      selectedAngles: ['预设俯视', '预设俯视', '预设俯视', '预设仰视', '预设仰视'],
      angleOptions: ['自定义', '左侧俯视', '右侧俯视', '左侧平视', '右侧平视', '左侧仰视', '右侧仰视', '预设仰视', '预设俯视'],
      showAngleMenus: [false, false, false, false, false],
      // 画面角度参数映射表
      angleParamsMap: {
        左侧俯视: { horizontal_angle: 335, vertical_angle: 20, zoom: 5 },
        右侧俯视: { horizontal_angle: 30, vertical_angle: 20, zoom: 5 },
        左侧平视: { horizontal_angle: 335, vertical_angle: 0, zoom: 5 },
        右侧平视: { horizontal_angle: 30, vertical_angle: 0, zoom: 5 },
        左侧仰视: { horizontal_angle: 335, vertical_angle: -15, zoom: 5 },
        右侧仰视: { horizontal_angle: 30, vertical_angle: -15, zoom: 5 },
        预设仰视: { horizontal_angle: 20, vertical_angle: -10, zoom: 5 },
        预设俯视: { horizontal_angle: 345, vertical_angle: 20, zoom: 5 }
      }
    }
  },
  computed: {
    canSend() {
      return this.uploadImages.length > 0
    }
  },
  created() {
    if (this.initialChatHistory && this.initialChatHistory.length > 0) {
      this.multiAngleChatHistory = [...this.initialChatHistory]
    }
  },
  mounted() {
    // 点击外部关闭下拉菜单
    document.addEventListener('click', this.closeAllMenus)
    // 监听重新生成事件
    this.$root.$on('multi-angle-regenerate', this.handleRegenerateFromParent)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeAllMenus)
    // 移除事件监听
    this.$root.$off('multi-angle-regenerate', this.handleRegenerateFromParent)
  },
  methods: {
    // 处理图片上传
    handleImageUpload() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.multiple = true // 允许多选
      input.onchange = async (event) => {
        const files = Array.from(event.target.files)
        if (files.length > 0) {
          await this.processImages(files)
        }
      }
      input.click()
    },

    // 处理图片文件
    async processImages(files) {
      try {
        Message.info('正在上传图片...')
        const ossClient = await getOssClient()
        const tmp = '/input/'
        const uploadedUrls = []

        for (const file of files) {
          const fileName = `multi_angle_${Date.now()}_${file.name}`
          const result = await ossClient.put(tmp + fileName, file)

          if (result.name) {
            const imageUrl = `https://mj-comfy-ui.oss-cn-shenzhen.aliyuncs.com/${result.name}`
            uploadedUrls.push(imageUrl)
          }
        }

        console.log('OSS上传结果:', uploadedUrls)

        if (uploadedUrls.length > 0) {
          this.uploadImages = uploadedUrls
          Message.success(`成功上传 ${uploadedUrls.length} 张图片!`)
        } else {
          Message.error('图片上传失败，请重试!')
        }
      } catch (error) {
        console.error('上传图片失败:', error)
        Message.error('图片上传失败，请检查网络!')
      }
    },

    // 移除图片
    removeImages() {
      this.uploadImages = []
    },

    // 关闭所有下拉菜单
    closeAllMenus() {
      this.showAngleMenus = [false, false, false, false, false]
    },

    // 切换高级选项悬浮窗
    toggleAdvancedPanel() {
      this.showAdvancedPanel = !this.showAdvancedPanel
      // 如果打开弹窗，在下一帧初始化所有相机参数
      if (this.showAdvancedPanel) {
        this.$nextTick(() => {
          this.initAllCameraWidgets()
        })
      }
    },

    // 初始化所有相机组件的参数
    initAllCameraWidgets() {
      for (let i = 0; i < 5; i++) {
        const selectedAngle = this.selectedAngles[i]
        const widgetRef = this.$refs[`cameraWidget${i + 1}`]
        if (widgetRef) {
          // 获取对应的参数（预设或自定义）
          const params = this.angleParamsMap[selectedAngle] || this.cameraParams[i]
          widgetRef.setCameraParams(params)
          console.log(`初始化相机${i + 1}参数:`, selectedAngle, params)
        }
      }
    },

    // 切换画面角度菜单
    toggleAngleMenu(index) {
      // 先关闭其他菜单
      const newMenus = [false, false, false, false, false]
      newMenus[index] = !this.showAngleMenus[index]
      this.showAngleMenus = newMenus
    },

    // 选择画面角度
    selectAngle(index, option) {
      this.$set(this.selectedAngles, index, option)
      this.showAngleMenus = [false, false, false, false, false]
      console.log(`选择角度${index + 1}:`, option)

      // 如果选择的不是"自定义"，则自动更新对应相机的参数
      if (option !== '自定义' && this.angleParamsMap[option]) {
        const params = this.angleParamsMap[option]
        this.$set(this.cameraParams, index, { ...params })
        console.log(`更新相机${index + 1}参数:`, params)

        // 如果高级选项弹窗已打开，同步更新3D相机组件
        const widgetRef = this.$refs[`cameraWidget${index + 1}`]
        if (this.showAdvancedPanel && widgetRef) {
          widgetRef.setCameraParams(params)
        }
      }
    },

    // 处理来自父组件的重新生成请求
    handleRegenerateFromParent(regenerateData) {
      if (!regenerateData) return
      console.log('接收到重新生成请求:', regenerateData)
      // 填充图片
      this.uploadImages = regenerateData.images || []
      // 清除 sessionStorage
      sessionStorage.removeItem('multi_angle_regenerate')
      // 自动触发生成
      this.$nextTick(() => {
        this.startTask()
      })
    },
    // 导航栏状态
    handleScroll(val) {
      // 不再动态改变导航栏宽度，图片和按钮始终显示
      this.scrollStatus = val
    },
    // 回滚到底部
    scrollToBottom() {
      if (this.scrollStatus) {
        this.$emit('scrollToBottom')
      }
    },

    // 处理3D相机参数变化（用户手动调节3D窗口时触发）
    handleCameraChange(index, params) {
      this.$set(this.cameraParams, index - 1, { ...params })
      // 用户手动调节了3D窗口，将对应的画面角度改为"自定义"
      if (this.selectedAngles[index - 1] !== '自定义') {
        this.$set(this.selectedAngles, index - 1, '自定义')
        console.log(`画面角度${index} 已切换为自定义`)
      }
      console.log(`相机${index}参数更新:`, params)
    },

    // 处理滑块参数变化（滑块拖动时触发）
    handleSliderChange(cameraIndex, paramName, value) {
      const paramIndex = cameraIndex - 1
      const numValue = paramName === 'zoom' ? parseFloat(value) : parseInt(value)
      const newParams = { ...this.cameraParams[paramIndex], [paramName]: numValue }
      this.$set(this.cameraParams, paramIndex, newParams)

      // 同步更新3D相机组件
      const widgetRef = this.$refs[`cameraWidget${cameraIndex}`]
      if (widgetRef) {
        widgetRef.setCameraParams(newParams)
      }

      // 切换为自定义
      if (this.selectedAngles[paramIndex] !== '自定义') {
        this.$set(this.selectedAngles, paramIndex, '自定义')
      }
    },

    // 替换工作流中的动态变量
    replaceWorkflowVariables(workflow, seed, imageUrl) {
      const workflowCopy = JSON.parse(JSON.stringify(workflow))
      console.log('🔍 开始替换工作流变量...')
      console.log('参数:', { seed, imageUrl, cameraParams: this.cameraParams })

      // 生成动态文件名（必须是JSON数组格式的字符串）
      const timestamp = Date.now()
      const randomNum = Math.floor(Math.random() * 99999)
      const outputFilenames = [
        `["multiangle/output-${timestamp}_${randomNum}_1.png"]`,
        `["multiangle/output-${timestamp}_${randomNum}_2.png"]`,
        `["multiangle/output-${timestamp}_${randomNum}_3.png"]`,
        `["multiangle/output-${timestamp}_${randomNum}_4.png"]`,
        `["multiangle/output-${timestamp}_${randomNum}_5.png"]`
      ]

      // OSS 配置
      const ossConfig = {
        accessKeyId: OSS_ACCESS_KEY_ID,
        accessKeySecret: OSS_ACCESS_KEY_SECRET
      }

      // 获取3D相机参数
      const cameraNodeIds = ['95', '100', '101', '102', '146']
      // 遍历所有节点替换变量
      for (const nodeId in workflowCopy) {
        const node = workflowCopy[nodeId]
        // 如果是相机节点，使用对应的3D相机参数
        const cameraIndex = cameraNodeIds.indexOf(nodeId)
        if (cameraIndex !== -1) {
          if (node.inputs) {
            node.inputs.horizontal_angle = this.cameraParams[cameraIndex].horizontal_angle
            node.inputs.vertical_angle = this.cameraParams[cameraIndex].vertical_angle
            node.inputs.zoom = this.cameraParams[cameraIndex].zoom
            console.log(`✅ 更新相机节点${nodeId}(相机${cameraIndex + 1})参数:`, {
              horizontal_angle: this.cameraParams[cameraIndex].horizontal_angle,
              vertical_angle: this.cameraParams[cameraIndex].vertical_angle,
              zoom: this.cameraParams[cameraIndex].zoom
            })
          }
        }
        if (node.inputs) {
          for (const inputKey in node.inputs) {
            const inputValue = node.inputs[inputKey]
            if (typeof inputValue === 'string') {
              if (inputValue === '{{SEED}}') {
                node.inputs[inputKey] = seed
                console.log(`✅ 节点${nodeId}.${inputKey}: {{SEED}} -> ${seed}`)
              } else if (inputValue === '{{INPUT_IMAGE_URL}}') {
                node.inputs[inputKey] = imageUrl
                console.log(`✅ 节点${nodeId}.${inputKey}: {{INPUT_IMAGE_URL}} -> ${imageUrl}`)
              } else if (inputValue === '{{OSS_ACCESS_KEY_ID}}') {
                node.inputs[inputKey] = ossConfig.accessKeyId
                console.log(`✅ 节点${nodeId}.${inputKey}: {{OSS_ACCESS_KEY_ID}} -> ${ossConfig.accessKeyId}`)
              } else if (inputValue === '{{OSS_ACCESS_KEY_SECRET}}') {
                node.inputs[inputKey] = ossConfig.accessKeySecret
                console.log(`✅ 节点${nodeId}.${inputKey}: {{OSS_ACCESS_KEY_SECRET}} -> [已隐藏]`)
              } else if (inputValue === '{{OUTPUT_FILENAME_1}}') {
                node.inputs[inputKey] = outputFilenames[0]
                console.log(`✅ 节点${nodeId}.${inputKey}: {{OUTPUT_FILENAME_1}} -> ${outputFilenames[0]}`)
              } else if (inputValue === '{{OUTPUT_FILENAME_2}}') {
                node.inputs[inputKey] = outputFilenames[1]
                console.log(`✅ 节点${nodeId}.${inputKey}: {{OUTPUT_FILENAME_2}} -> ${outputFilenames[1]}`)
              } else if (inputValue === '{{OUTPUT_FILENAME_3}}') {
                node.inputs[inputKey] = outputFilenames[2]
                console.log(`✅ 节点${nodeId}.${inputKey}: {{OUTPUT_FILENAME_3}} -> ${outputFilenames[2]}`)
              } else if (inputValue === '{{OUTPUT_FILENAME_4}}') {
                node.inputs[inputKey] = outputFilenames[3]
                console.log(`✅ 节点${nodeId}.${inputKey}: {{OUTPUT_FILENAME_4}} -> ${outputFilenames[3]}`)
              } else if (inputValue === '{{OUTPUT_FILENAME_5}}') {
                node.inputs[inputKey] = outputFilenames[4]
                console.log(`✅ 节点${nodeId}.${inputKey}: {{OUTPUT_FILENAME_5}} -> ${outputFilenames[4]}`)
              }
            }
          }
        }
      }

      console.log('🔍 替换后的工作流:', JSON.stringify(workflowCopy, null, 2))
      return workflowCopy
    },

    // 开始任务
    async startTask() {
      if (!this.canSend || this.processing) {
        return
      }

      // 确保放大状态
      if (!this.scrollStatus) {
        this.handleScroll(true)
      }

      if (this.uploadImages.length === 0) {
        Message.warning('请先上传图片!')
        return
      }

      const userImages = [...this.uploadImages]

      // 清空输入
      this.uploadImages = []

      // 添加用户消息到历史
      this.multiAngleChatHistory.push({
        role: 'user',
        content: '多角度生成',
        images: userImages,
        timestamp: new Date().toISOString()
      })

      // 更新界面
      this.$emit('chatUpdate', this.multiAngleChatHistory)
      this.$emit('aiTyping', true)

      this.processing = true

      // 清空 uploadImages 后立即强制保持放大状态
      this.$nextTick(() => {
        this.handleScroll(true)
      })

      try {
        // 生成随机种子
        const seed = Math.floor(Math.random() * 100000000000000)
        console.log('🎲 生成的种子:', seed)
        console.log('🖼️ 图片URL:', userImages[0])

        // 使用实际的多角度生成工作流
        const promptData = this.replaceWorkflowVariables(
          multiAngleWorkflow,
          seed,
          userImages[0]
        )

        console.log('👍 工作流变量替换完成')

        this.clientId = uuidv4()
        const data = {
          prompt: promptData,
          client_id: this.clientId
        }

        console.log('🚀 准备发送多角度生成任务...')
        console.log('📦 客户端ID:', this.clientId)

        // 发送 Prompt 请求
        const result = await genPrompt(data)

        console.log('📨 genPrompt 返回结果:', result)

        if (result && result.prompt_id) {
          console.log('✅ 任务提交成功，promptId:', result.prompt_id)
          // 监听 WebSocket 进度
          await this.listenToMultiAngleProgress(result.prompt_id)
        } else {
          throw new Error('任务提交失败')
        }
      } catch (error) {
        console.error('❗ 多角度生成失败:', error)
        let errorMessage = '多角度生成失败，请稍后再试!'
        if (error.response) {
          console.error('⚠️ 服务器返回错误:', error.response.status)
          console.error('错误详情:', JSON.stringify(error.response.data, null, 2))
          errorMessage = `服务器错误: ${error.response.status}`
        } else if (error.message) {
          errorMessage = error.message
        }
        // 添加失败的 AI 回复到历史
        this.multiAngleChatHistory.push({
          role: 'assistant',
          content: '抱歉，似乎发生了某些问题',
          timestamp: new Date().toISOString(),
          isError: true // 标记为错误消息
        })
        // 发送对话历史给父组件
        this.$emit('chatUpdate', this.multiAngleChatHistory)
        this.$emit('aiTyping', false)
        Message.error(errorMessage)
        this.processing = false
      }
    },

    // 监听多角度生成进度
    async listenToMultiAngleProgress(promptId) {
      // 重置标志位
      this.resultFetched = false
      this.ws = new WebSocket(`${getLastUsedBaseUrl()}/ws?clientId=${this.clientId}`)
      const wsTimeout = setTimeout(() => {
        Message.warning('网络连接超时,请重新尝试!')
        this.ws.close()
      }, 2000)
      this.ws.onopen = () => {
        console.log('✅ WebSocket 连接成功')
        // 记录任务开始
        getTaskStart({
          type: MULTI_ANGLE,
          prompt: this.textarea,
          input_images: this.uploadImages
        }).then(rep => {
          this.taskLogId = rep.data.task.id
        })
        clearTimeout(wsTimeout)
      }
      this.ws.onmessage = async (event) => {
        clearTimeout(wsTimeout)
        const data = event.data
        if (typeof data === 'string') {
          try {
            const msg = JSON.parse(data)
            // 过滤掉监控消息，只显示关键信息
            if (msg.type !== 'crystools.monitor') {
              console.log('📡 收到消息:', msg)
            }

            if (msg.type === 'executing') {
              const execData = msg.data
              if (execData.node === null && execData.prompt_id === promptId) {
                console.log('✅ 多角度生成完成')
                this.currentExecutingNode = ''
                this.$emit('nodeUpdate', '')
                this.ws.close()
                // 防止重复调用
                if (!this.resultFetched) {
                  this.resultFetched = true
                  await this.fetchResultImages(promptId)
                }
              } else if (execData.node !== null) {
                console.log(`🔄 正在执行节点: ${execData.node}`)
                // 更新当前执行的节点名称
                const nodeName = this.nodeNameMap[execData.node] || `执行节点 ${execData.node}`
                this.currentExecutingNode = nodeName
                this.$emit('nodeUpdate', nodeName)
              }
            } else if (msg.type === 'progress') {
              const value = msg.data.value
              const max = msg.data.max
              const current = (value / max * 100).toFixed(2)
              console.log(`📈 进度: ${current}%`)
              // 发送进度给父组件
              this.$emit('progressUpdate', current)
            } else if (msg.type === 'status') {
              console.log('📊 状态更新:', msg.data)
              // 如果队列为空且没有执行中的任务，说明任务已经完成或跳过
              if (msg.data.status && msg.data.status.exec_info) {
                const execInfo = msg.data.status.exec_info
                if (execInfo.queue_remaining === 0) {
                  console.log('🤔 队列为空，检查任务状态...')
                  // 等待2秒后检查任务状态
                  setTimeout(async () => {
                    await this.checkTaskStatus(promptId)
                  }, 2000)
                }
              }
            } else if (msg.type === 'execution_error') {
              console.error('❌ 执行错误:', msg.data)
              // 添加失败的 AI 回复到历史
              this.multiAngleChatHistory.push({
                role: 'assistant',
                content: '抱歉，似乎发生了某些问题',
                timestamp: new Date().toISOString(),
                isError: true // 标记为错误消息
              })
              this.$emit('chatUpdate', this.multiAngleChatHistory)
              Message.error('多角度生成过程中出现错误，请查看控制台')
              this.ws.close()
              this.processing = false
              this.$emit('aiTyping', false)
            }
          } catch (error) {
            console.error('WebSocket消息解析失败:', error)
          }
        }
      }
      this.ws.onclose = async () => {
        console.log('🔌 WebSocket 连接关闭')
        try {
          const index = await cudaIndexNum()
          if (index) {
            await cuda.changeCudaCurrentStatus(index, 'leisure')
          }
        } catch (error) {
          console.log(error)
        }
      }
      this.ws.onerror = (error) => {
        console.error('❌ WebSocket 错误:', error)
        Message.error('WebSocket 连接错误')
        this.processing = false
        this.$emit('aiTyping', false)
      }
    },

    // 检查任务状态
    async checkTaskStatus(promptId) {
      try {
        console.log('🔍 检查任务状态:', promptId)
        const historyRequest = axios.create({
          baseURL: getLastUsedBaseUrl(),
          timeout: 300000,
          headers: {
            'Content-Type': 'application/json'
          }
        })
        const response = await historyRequest.get(`/history/${promptId}`)
        const promptInfo = response.data[promptId]
        console.log('📝 任务历史信息:', promptInfo)

        if (!promptInfo) {
          console.error('❌ 找不到任务信息，任务可能还在排队中...')
          return
        }

        // 检查任务状态
        if (promptInfo.status) {
          console.log('📊 任务状态:', promptInfo.status.status_str)

          if (promptInfo.status.status_str === 'success') {
            console.log('✅ 任务已完成，尝试获取结果图片')
            // 防止重复调用
            if (!this.resultFetched) {
              this.resultFetched = true
              await this.fetchResultImages(promptId)
            }
          } else if (promptInfo.status.status_str === 'error') {
            console.error('❌ 任务执行失败:', promptInfo.status)
            // 添加失败的 AI 回复到历史
            this.multiAngleChatHistory.push({
              role: 'assistant',
              content: '抱歉，似乎发生了某些问题',
              timestamp: new Date().toISOString(),
              isError: true // 标记为错误消息
            })
            this.$emit('chatUpdate', this.multiAngleChatHistory)
            Message.error('任务执行失败，请检查工作流配置')
            this.processing = false
            this.$emit('aiTyping', false)
            this.ws.close()
          }
        }
      } catch (error) {
        console.error('🚫 检查任务状态失败:', error)
      }
    },

    // 记录任务结束
    async recordTaskEnd(imageUrls) {
      try {
        const details = []
        for (const url of imageUrls) {
          const imgInfo = await getImageSizeByLoading(url)
          details.push({ path: url, imgInfo: imgInfo })
        }
        const info = { details: details }
        await getTaskEnd({ result: JSON.stringify(info) }, this.taskLogId)
      } catch (error) {
        console.error('记录任务结束失败:', error)
      }
    },
    // 获取结果图片URLs
    async fetchResultImages(promptId) {
      try {
        const historyRequest = axios.create({
          baseURL: getLastUsedBaseUrl(),
          timeout: 300000,
          headers: {
            'Content-Type': 'application/json'
          }
        })
        const response = await historyRequest.get(`/history/${promptId}`)
        const promptInfo = response.data[promptId]
        console.log('📄 完整的promptInfo:', promptInfo)

        if (promptInfo.status.status_str === 'success') {
          // 从工作流输出节点解析结果图片URL
          const resultImages = []

          // OSS上传节点ID列表
          const ossNodes = ['191', '192', '193', '194', '196']
          const promptIndex = 2 // prompt信息的下标

          try {
            for (const nodeId of ossNodes) {
              if (promptInfo.prompt &&
                promptInfo.prompt[promptIndex] &&
                promptInfo.prompt[promptIndex][nodeId] &&
                promptInfo.prompt[promptIndex][nodeId].inputs &&
                promptInfo.prompt[promptIndex][nodeId].inputs.filename) {
                const filenameStr = promptInfo.prompt[promptIndex][nodeId].inputs.filename
                const tmp = JSON.parse(filenameStr)
                if (tmp && tmp[0]) {
                  // 生成完整OSS URL
                  const imageUrl = `https://mj-comfy-ui.oss-cn-shenzhen.aliyuncs.com/${tmp[0]}`
                  resultImages.push(imageUrl)
                  console.log(`✅ 获取结果图片 ${nodeId}:`, imageUrl)
                }
              }
            }
          } catch (e) {
            console.error('解析结果图片失败:', e)
          }

          if (resultImages.length === 0) {
            console.error('❌ 未能从工作流输出中获取图片')
            // 添加失败的 AI 回复到历史
            this.multiAngleChatHistory.push({
              role: 'assistant',
              content: '抱歉，似乎发生了某些问题',
              timestamp: new Date().toISOString(),
              isError: true
            })
            this.$emit('chatUpdate', this.multiAngleChatHistory)
            Message.error('获取生成结果失败')
            this.processing = false
            this.$emit('aiTyping', false)
            return
          }

          // 添加AI回复到历史
          this.multiAngleChatHistory.push({
            role: 'assistant',
            content: `多角度生成已完成，已为你生成 ${resultImages.length} 张产品多角度展示图～!`,
            resultImages: resultImages,
            timestamp: new Date().toISOString()
          })
          this.$emit('chatUpdate', this.multiAngleChatHistory)
          this.$emit('aiTyping', false)
          this.processing = false
          // 记录任务结束
          await this.recordTaskEnd(resultImages)
          Message.success('多角度生成成功!')
        } else {
          console.error('任务状态异常:', promptInfo.status)
          const errorMsg = `任务执行失败: ${promptInfo.status.status_str}`
          // 添加失败的 AI 回复到历史
          this.multiAngleChatHistory.push({
            role: 'assistant',
            content: '抱歉，似乎发生了某些问题',
            timestamp: new Date().toISOString(),
            isError: true // 标记为错误消息
          })
          this.$emit('chatUpdate', this.multiAngleChatHistory)
          Message.error(errorMsg)
          this.processing = false
          this.$emit('aiTyping', false)
        }
      } catch (error) {
        console.error('获取结果失败：', error)
        console.error('错误详情:', error.response?.data || error.message)
        // 添加失败的 AI 回复到历史
        this.multiAngleChatHistory.push({
          role: 'assistant',
          content: '抱歉，似乎发生了某些问题',
          timestamp: new Date().toISOString(),
          isError: true // 标记为错误消息
        })
        this.$emit('chatUpdate', this.multiAngleChatHistory)
        Message.error('获取生成结果失败，请查看控制台')
        this.processing = false
        this.$emit('aiTyping', false)
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import "@/style/common.less";
@import "@/assets/fonts/style.css";
@import "@/assets/font_sczlgzino/iconfont.css";

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  font-family: "icomoon";
  overflow: hidden;
}

.iconfont {
  font-family: "iconfont" !important;
  font-size: 25px;
  font-style: normal;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.active {
  background: @basicselect;
}

.navigation {
  position: absolute;
  width: calc(100vw - 80px);
  bottom: 15px;
  left: 80px;
  z-index: 8;
  transition: all 0.5s;
  box-sizing: border-box;

  .navigation_bottom {
    position: relative;
    width: 100%;
    max-width: 1400px;
    height: 50px;
    font-size: 23px;
    margin: 0 auto;
    justify-content: space-between;
    margin-bottom: 10px;

    .ai_assistant {
      position: absolute;
      bottom: -10px;
      left: 280px;
      width: 120px;
      height: 120px;
      opacity: 1;
      transition: all 0.5s;
    }

    .scroll_bottom {
      position: absolute;
      right: 280px;
      font-size: 16px;
      color: @font_white;
      background-color: #c1c1c1;
      padding: 10px 15px;
      cursor: pointer;
      border-radius: @border_radius_3;
      transition: all 0.5s;
      opacity: 1;
    }
  }

  .navigation_items {
    position: relative;
    width: 100%;
    max-width: 900px;
    height: 110px;
    display: flex;
    flex-direction: column;
    background: rgba(235, 235, 235, 0.5);
    backdrop-filter: blur(10px);
    border: 1px solid @font_white;
    border-radius: 25px;
    overflow: visible;
    transition: all 0.3s;
    margin: 0 auto;
    box-shadow: 0 0 10px 0px rgba(0, 0, 0, 0.2);

    .main_input {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      padding: 0 20px;
      gap: 45px;

      // 图片上传区域
      .upload-section {
        position: relative;
        width: 100px;
        height: 80px;
        border: 2px dashed #d0d0d0;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s;
        flex-shrink: 0;
        overflow: hidden;
        background: white;
        margin-left: 25px;

        &:hover {
          border-color: @morejoy;
          background: #fff8f3;
        }

        .upload-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          height: 100%;

          .upload-plus {
            font-size: 36px;
            color: #999;
            font-weight: 300;
            line-height: 1;
          }

          .upload-label {
            font-size: 12px;
            color: #666;
            font-weight: 400;
          }
        }

        .uploaded-preview {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #000;

          .thumbnail-img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .image-count {
            position: absolute;
            bottom: 5px;
            right: 5px;
            background: rgba(255, 140, 66, 0.9);
            color: white;
            padding: 2px 8px;
            border-radius: 10px;
            font-size: 12px;
            font-weight: 500;
          }

          .remove-btn {
            position: absolute;
            top: 5px;
            right: 5px;
            width: 20px;
            height: 20px;
            background: rgba(0, 0, 0, 0.6);
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            cursor: pointer;
            transition: all 0.3s;

            &:hover {
              background: rgba(255, 0, 0, 0.8);
              transform: scale(1.1);
            }
          }
        }
      }

      // 3D相机控制窗口
      .camera-widget-wrapper {
        width: 140px;
        height: 140px;
        border-radius: 12px;
        overflow: hidden;
        background: #ffffff;
        border: 2px dashed #d0d0d0;
        flex-shrink: 0;
        box-shadow: none;
        transition: all 0.3s;

        &:hover {
          border-color: @morejoy;
        }
      }

      textarea {
        flex: 1;
        font-family: '微软雅黑';
        font-size: 23px;
        padding: 20px;
        border: none;
        background: none;
        resize: none;
        outline: none;
      }

      .sent {
        position: absolute;
        right: 15px;
        width: 60px;
        height: 60px;
        text-align: center;
        line-height: 60px;
        font-size: 35px;
        color: @font_white;
        border: none;
        border-radius: 30px;
        background-color: @font_gray;
        cursor: pointer;
        opacity: 0.7;
        transition: all 0.3s;

        &.has_input {
          background-color: @morejoy; // 有输入时变成橙色
          opacity: 1;
        }

        &:hover:not(:disabled) {
          opacity: 1;
          font-size: 38px;
        }

        &:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .rotating {
          animation: rotate 1s linear infinite;
        }
      }
    }

    /* 放大后的发送按钮（绝对定位） */
    .sent_expanded {
      position: absolute;
      right: 15px;
      top: 50%;
      transform: translateY(-50%);
      width: 60px;
      height: 60px;
      text-align: center;
      line-height: 60px;
      font-size: 35px;
      color: @font_white;
      border: none;
      border-radius: 30px;
      background-color: @font_gray;
      cursor: pointer;
      opacity: 0.7;
      transition: all 0.3s;
      z-index: 10;

      &.has_input {
        background-color: @morejoy; // 有输入时变成橙色
        opacity: 1;
      }

      &:hover:not(:disabled) {
        opacity: 1;
        font-size: 38px;
      }

      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }

      .rotating {
        animation: rotate 1s linear infinite;
      }
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// 高级选项弹窗遮罩（与图片预览弹窗形式一致）
.advanced-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(128, 128, 128, 0.5);
  backdrop-filter: blur(20px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;

  .advanced-panel-content {
    position: relative;
    background: white;
    border-radius: 20px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
    padding: 24px 30px 30px;
    animation: zoomIn 0.3s ease;

    .advanced-panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;

      .panel-title {
        font-size: 16px;
        font-weight: 600;
        color: #333;
      }

      .panel-close-btn {
        width: 36px;
        height: 36px;
        background: #f8f9fa;
        color: #6c757d;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 28px;
        line-height: 1;
        cursor: pointer;
        transition: all 0.3s;
        user-select: none;

        &:hover {
          background: #ff8c42;
          color: white;
          transform: scale(1.1);
        }
      }
    }

    .advanced-panel-body {
      display: flex;
      gap: 16px;
      align-items: flex-start;

      .camera-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;

        .camera-label {
          font-size: 12px;
          color: #888;
          font-weight: 500;
        }

        .camera-widget-wrapper {
          width: 300px;
          height: 300px;
          border-radius: 12px;
          overflow: hidden;
          background: #f5f5f5;
          border: 2px dashed #d0d0d0;
          flex-shrink: 0;
          transition: all 0.3s;

          &:hover {
            border-color: @morejoy;
          }
        }

        .camera-sliders {
          width: 280px;
          padding: 10px 12px;
          background: #f8f9fa;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 8px;

          .slider-row {
            display: flex;
            align-items: center;
            gap: 8px;

            .slider-label {
              font-size: 11px;
              color: #666;
              min-width: 52px;
              white-space: nowrap;
              font-weight: 400;
            }

            .camera-slider {
              flex: 1;
              height: 4px;
              -webkit-appearance: none;
              appearance: none;
              background: #ddd;
              border-radius: 2px;
              outline: none;
              cursor: pointer;

              &::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                width: 14px;
                height: 14px;
                border-radius: 50%;
                background: @morejoy;
                cursor: pointer;
                border: 2px solid white;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
                transition: transform 0.15s ease;

                &:hover {
                  transform: scale(1.2);
                }
              }

              &::-moz-range-thumb {
                width: 14px;
                height: 14px;
                border-radius: 50%;
                background: @morejoy;
                cursor: pointer;
                border: 2px solid white;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
              }
            }

            .slider-value {
              font-size: 11px;
              color: #333;
              font-weight: 500;
              min-width: 36px;
              text-align: right;
              white-space: nowrap;
            }
          }
        }
      }
    }
  }
}

// 弹窗过渡动画
.panel-fade-enter-active,
.panel-fade-leave-active {
  transition: opacity 0.25s ease;
}

.panel-fade-enter,
.panel-fade-leave-to {
  opacity: 0;
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.92);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

// 画面角度选择按钮组
.angle-buttons-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;

  .angle-row {
    display: flex;
    gap: 40px;
  }

  .angle-dropdown {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    width: 165px;
    box-sizing: border-box;
    background: white;
    border: 1px solid #d0d0d0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;
    user-select: none;

    &:hover {
      border-color: @morejoy;
      background: #fff8f3;
    }

    .option-label {
      font-size: 12px;
      color: #999;
      font-weight: 400;
    }

    .option-value {
      font-size: 13px;
      color: #333;
      font-weight: 500;
      max-width: 60px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .arrow {
      font-size: 12px;
      color: #999;
      transition: transform 0.3s;
    }

    .dropdown-menu {
      position: absolute;
      bottom: calc(100% + 5px);
      left: 0;
      min-width: 100px;
      background: white;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      padding: 6px;
      z-index: 1000;
      animation: fadeIn 0.2s ease;

      .menu-item {
        padding: 8px 10px;
        font-size: 12px;
        color: #333;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.3s;
        text-align: center;
        white-space: nowrap;

        &:hover {
          background: #fff8f3;
          color: @morejoy;
        }

        &.active {
          background: @morejoy;
          color: white;
          font-weight: 500;
        }
      }
    }
  }
}

// 高级选项按钮（与角度按钮样式一致）
.advanced-options-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  width: 165px;
  background: white;
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  user-select: none;
  flex-shrink: 0;
  box-sizing: border-box;

  &:hover {
    border-color: @morejoy;
    background: #fff8f3;
  }

  .center-text {
    font-size: 13px;
    color: #333;
    font-weight: 500;
    text-align: center;
  }
}
</style>
